import React from 'react';

import classNames from 'classnames';

import { GenericLink } from './commons';

import { ActionDisabledWithTooltip } from '../Buttons/ActionDisabledWithTooltip';

import type { GenericLinkProps, LinkSize } from './commons';
import type { Placement } from '@floating-ui/react';
import type { Argument } from 'classnames';

const linkClassnamesByVariant: { [key in LinkVariant]: string[] } = {
  primary: [
    'text-brand',
    'hover:action-text-primary-strong-hover',
    'active:action-text-primary-strong-pressed',
  ],
  secondary: ['text-primary', 'hover:text-primary', 'active:text-primary'],
  darkmode: ['text-inverse', 'hover:text-inverse', 'active:text-inverse'],
};

const linkClassnamesBySize: { [key in LinkSize]: string } = {
  lg: 'text-b2',
  md: 'text-b2',
  sm: 'text-b3',
  xs: 'text-b3',
};

export type LinkVariant = 'primary' | 'secondary' | 'darkmode';

export const makeLinkStyles = (
  variant: LinkVariant,
  size: LinkSize,
  disabled?: boolean,
  inline?: boolean,
): Argument[] => [
  ...linkClassnamesByVariant[variant],
  linkClassnamesBySize[size],
  'cursor-pointer inline-flex items-center font-normal',
  'hover:underline',
  'active:outline-0',
  'focus:outline-green-400 focus:outline focus:outline-2',
  { 'text-disabled! pointer-events-none': disabled },
  { underline: inline },
];

export const LinkIconContainer: React.FC<{ icon: React.ReactElement }> = ({ icon }) => (
  <span className="h-md w-md ml-xxs inline-flex justify-center">{icon}</span>
);

export type CustomLinkProps = {
  inline?: boolean;
  disabled?: boolean;
  icon?: React.ReactElement;
  variant?: LinkVariant;
  size?: LinkSize;
  disabledMessageTooltip?: string;
  disabledTriggerClassName?: string;
  positionTooltip?: Placement;
};

type Props = CustomLinkProps & GenericLinkProps;

export const Link: React.FC<Props> = ({
  children,
  variant = 'primary',
  inline,
  disabled = false,
  disabledMessageTooltip,
  disabledTriggerClassName,
  positionTooltip,
  className,
  icon,
  size = 'md',
  ...rest
}) => {
  const linkClassName: string = classNames(
    className,
    ...makeLinkStyles(variant, size, disabled, inline),
  );
  const maybeIcon = icon && <LinkIconContainer icon={icon} />;

  return (
    <ActionDisabledWithTooltip
      disabled={disabled}
      disabledMessage={disabledMessageTooltip}
      position={positionTooltip}
      disabledTriggerClassName={disabledTriggerClassName}
    >
      <GenericLink classNames={linkClassName} disabled={disabled} {...rest}>
        {children}
        {maybeIcon}
      </GenericLink>
    </ActionDisabledWithTooltip>
  );
};
